import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';

interface HasBackgroundProcess {
  backgroundProcess?: BackgroundProcess;
}

type Props = {
  clientId?: string;
  resource: string;
  enabled?: boolean;
  params?: Record<string, string | boolean>;
};

export const useProcessingDataExportDocument = <T extends HasBackgroundProcess>({
  clientId: overrideClientId,
  resource,
  enabled = true,
  params,
}: Props) => {
  const { clientId: defaultClientId } = useCurrentClient();
  const clientId = overrideClientId ?? defaultClientId;
  const [firstRequestTime, setFirstRequestTime] = useState<Date>(new Date());
  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);

  const { data, isLoading, isError, error, refetch } = useQuery<T[]>({
    queryKey: params ? ['clients', clientId, resource, params] : ['clients', clientId, resource],
    enabled: enabled && !!clientId,
    refetchInterval,
  });

  const items = data || [];
  const secondsSinceFirstRequest = (new Date().getTime() - firstRequestTime.getTime()) / 1000;
  const waitingForItems = secondsSinceFirstRequest < 30 && items.length === 0;

  useEffect(() => {
    setFirstRequestTime(new Date());
  }, [clientId]);

  useEffect(() => {
    const isProcessing = items.some(
      agenda =>
        agenda.backgroundProcess &&
        ['PROCESSING', 'PENDING'].includes(agenda.backgroundProcess.status),
    );

    if (isProcessing || waitingForItems) {
      setRefetchInterval(5000);
    } else {
      setRefetchInterval(false);
    }
  }, [items, waitingForItems]);

  return {
    items,
    isLoading,
    isError,
    error,
    refetch,
  };
};
